'use client';
import { API_ROUTES, QueryBoundary, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useDebug } from '@shared/debug';
import { twMerge } from '@shared/themes';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useId, type ComponentProps, type ReactNode } from 'react';
import { HorizontalScrollContainer } from '../HorizontalScrollContainer/HorizontalScrollContainer';
import { PromotionPod } from '../PromotionPod/PromotionPod';
import { mapOffer } from '../promotions/utils/mapOffer/mapOffer';
import type { OfferBundlePromotion } from '../types';

type PromotionsCarouselProps = Omit<ComponentProps<'div'>, 'children'> & {
  namespace?: string;
  moreInfoCta?: ReactNode;
};

// A carousel needs at least two promotions to be worth showing.
const MIN_OFFERS_FOR_CAROUSEL = 2;

/**
 * A horizontally-scrollable carousel of the player's available promotions (offers).
 *
 * Fetches offers and renders them as {@link PromotionPod} cards inside an accessible list. It
 * intentionally renders nothing while loading — so it never reserves layout height before we know
 * there is anything to show — logs and renders nothing on fetch failure, and also renders nothing
 * unless there are at least two promotions to scroll through.
 *
 * @param namespace - Optional translations namespace for the optional section heading. The heading (and
 *   `moreInfoCta`) render only when this namespace has a `title` key.
 * @param moreInfoCta - Optional node (e.g. a "View all" link) shown next to the heading.
 * @param className - Additional classes for the outer container.
 * @param props - Standard div props spread onto the outer container (except `children`).
 * @returns The carousel, or nothing while loading, on error, or with fewer than two promotions.
 */
function PromotionsCarousel({ className, namespace, moreInfoCta, ...props }: PromotionsCarouselProps) {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const { apiLocale, baseImgUrl } = useClientConfig();
  const availableRoute = (isLoggedIn ? API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PRIVATE : API_ROUTES.PROMOTIONS.OFFERS_AND_REWARDS.PUBLIC).replace(':locale', apiLocale);
  const query = useApiQuery<OfferBundlePromotion[]>(availableRoute, {
    credentials: true,
    queryOptions: { staleTime: 0 },
  });
  const t = useTranslations('offers.page');
  const tSection = useTranslations(namespace);
  const debug = useDebug();
  const titleId = useId();
  const hasSectionTitle = tSection.has('title');

  return (
    <QueryBoundary
      query={query}
      loadingFallback={null}
      errorFallbackRender={({ error }) => {
        debug.forceError('[PromotionsCarousel] Failed to fetch promotions', error);
        return null;
      }}
    >
      {({ data }) => {
        if (!data || data.length < MIN_OFFERS_FOR_CAROUSEL) return null;
        const mappedAvailableOffers = data.map(item => mapOffer({ item, t, isPreviousOffer: false, baseImgUrl }));
        return (
          <div className={twMerge('ll-grid-container', className)} data-track-container="Promotions" {...props}>
            {hasSectionTitle ? (
              <div className="ll-container-inner mb-5 flex items-center justify-between">
                <h4 id={titleId}>{tSection('title')}</h4>
                {moreInfoCta ?? null}
              </div>
            ) : null}

            <div className="ll-container-full lg:ll-container-inner">
              <HorizontalScrollContainer
                role="list"
                aria-labelledby={hasSectionTitle ? titleId : undefined}
                className="px-[calc(var(--ll-container-padding)+2px)] lg:px-0.5 py-0.5 -m-0.5 gap-4 md:gap-6"
                data-track-container="Carousel"
                data-track-carousel-title="Promotions"
              >
                {mappedAvailableOffers.map(offer => (
                  <PromotionPod
                    key={offer.id}
                    role="listitem"
                    data={offer}
                    variant="offers"
                    markAsReadOnMount={false}
                    className="w-75 md:w-102.5 shrink-0"
                    data-track-container="Promotion"
                    data-track-id={offer.id}
                  />
                ))}
              </HorizontalScrollContainer>
            </div>
          </div>
        );
      }}
    </QueryBoundary>
  );
}

export { PromotionsCarousel };
